import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, Sparkles, ShoppingBag, Package, ArrowRight, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface FirstTimeWizardProps {
  userRole: "buyer" | "supplier" | "admin";
  onComplete: () => void;
  onSkip: () => void;
}

export function FirstTimeWizard({ userRole, onComplete, onSkip }: FirstTimeWizardProps) {
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const { toast } = useToast();

  const totalSteps = 3;
  const progress = (step / totalSteps) * 100;

  const buyerSteps = [
    {
      title: "Welcome to HoleScale",
      description: "Your B2B marketplace for custom packaging solutions",
      icon: Sparkles,
      content: (
        <div className="space-y-4 text-center">
          <p className="text-base text-muted-foreground">
            Discover suppliers, request quotes, and manage orders all in one place. Get started in minutes.
          </p>
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="rounded-lg border border-primary/10 bg-primary/5 p-4 transition-colors hover:bg-primary/10">
              <CheckCircle2 className="mx-auto mb-2 size-8 text-primary" />
              <p className="text-sm font-medium">Browse Products</p>
              <p className="mt-1 text-xs text-muted-foreground">Find suppliers</p>
            </div>
            <div className="rounded-lg border border-primary/10 bg-primary/5 p-4 transition-colors hover:bg-primary/10">
              <CheckCircle2 className="mx-auto mb-2 size-8 text-primary" />
              <p className="text-sm font-medium">Request Quotes</p>
              <p className="mt-1 text-xs text-muted-foreground">Get pricing</p>
            </div>
            <div className="rounded-lg border border-primary/10 bg-primary/5 p-4 transition-colors hover:bg-primary/10">
              <CheckCircle2 className="mx-auto mb-2 size-8 text-primary" />
              <p className="text-sm font-medium">Track Orders</p>
              <p className="mt-1 text-xs text-muted-foreground">Stay updated</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Complete Your Profile",
      description: "Add your company details to get started",
      icon: ShoppingBag,
      content: (
        <div className="space-y-4">
          <p className="text-center text-base text-muted-foreground">
            A complete profile helps suppliers understand your needs better and provide accurate quotes. This takes less than 2 minutes.
          </p>
          <div className="space-y-2 rounded-lg bg-muted/50 p-4 text-sm">
            <p className="font-medium">What you'll add:</p>
            <ul className="list-inside list-disc space-y-1 text-muted-foreground">
              <li>Company name and location</li>
              <li>Contact information</li>
              <li>Business preferences</li>
            </ul>
          </div>
          <Button 
            className="w-full" 
            onClick={() => {
              const profilePath = userRole === 'admin' ? '/admin/profile' :
                                 userRole === 'supplier' ? '/supplier/profile' :
                                 '/buyer/profile';
              navigate(`${profilePath}?setup=true`);
              onComplete();
            }}
          >
            Complete Profile Now
          </Button>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => setStep(3)}
          >
            I'll Do This Later
          </Button>
        </div>
      ),
    },
    {
      title: "You're All Set!",
      description: "Start exploring products and suppliers",
      icon: CheckCircle2,
      content: (
        <div className="space-y-4 text-center">
          <p className="text-muted-foreground">
            Everything is ready. You can now browse products, post RFQs, and connect with suppliers.
          </p>
          <div className="mt-6 flex justify-center gap-4">
            <Button onClick={() => { 
              navigate(userRole === 'supplier' ? "/supplier/products" : "/buyer/products"); 
              onComplete(); 
            }}>
              Browse Products
            </Button>
            <Button variant="outline" onClick={() => { navigate("/buyer/rfq/new"); onComplete(); }}>
              Post an RFQ
            </Button>
          </div>
        </div>
      ),
    },
  ];

  const supplierSteps = [
    {
      title: "Welcome to HoleScale",
      description: "Connect with buyers and grow your business",
      icon: Sparkles,
      content: (
        <div className="space-y-4 text-center">
          <p className="text-muted-foreground">
            Showcase your products, respond to RFQs, and manage orders seamlessly.
          </p>
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="bg-info/5/50 rounded-lg p-4">
              <CheckCircle2 className="mx-auto mb-2 size-8 text-primary" />
              <p className="text-sm font-medium">Add Products</p>
            </div>
            <div className="bg-info/5/50 rounded-lg p-4">
              <CheckCircle2 className="mx-auto mb-2 size-8 text-primary" />
              <p className="text-sm font-medium">Submit Quotes</p>
            </div>
            <div className="bg-info/5/50 rounded-lg p-4">
              <CheckCircle2 className="mx-auto mb-2 size-8 text-primary" />
              <p className="text-sm font-medium">Fulfill Orders</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Set Up Your Catalog",
      description: "Add products to start receiving orders",
      icon: Package,
      content: (
        <div className="space-y-4">
          <p className="text-center text-base text-muted-foreground">
            Your product catalog is your storefront. Add detailed listings to attract more buyers and get matched with relevant RFQs.
          </p>
          <div className="space-y-2 rounded-lg bg-muted/50 p-4 text-sm">
            <p className="font-medium">Benefits of a complete catalog:</p>
            <ul className="list-inside list-disc space-y-1 text-muted-foreground">
              <li>Get matched with relevant RFQs automatically</li>
              <li>Showcase your capabilities to buyers</li>
              <li>Receive more quote requests</li>
            </ul>
          </div>
          <Button 
            className="w-full"
            onClick={() => {
              navigate("/supplier/products");
              onComplete();
            }}
          >
            Add Products Now
          </Button>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => setStep(3)}
          >
            I'll Do This Later
          </Button>
        </div>
      ),
    },
    {
      title: "You're All Set!",
      description: "Start receiving RFQs and orders",
      icon: CheckCircle2,
      content: (
        <div className="space-y-4 text-center">
          <p className="text-muted-foreground">
            Everything is ready. You can now browse RFQs and start receiving orders.
          </p>
          <div className="mt-6 flex justify-center gap-4">
            <Button onClick={() => { navigate("/supplier/rfq-marketplace"); onComplete(); }}>
              Browse RFQs
            </Button>
            <Button variant="outline" onClick={() => { navigate("/supplier/products"); onComplete(); }}>
              Add Products
            </Button>
          </div>
        </div>
      ),
    },
  ];

  const steps = userRole === "buyer" ? buyerSteps : supplierSteps;
  const currentStep = steps[step - 1];
  const Icon = currentStep.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
      <Card className="relative w-full max-w-2xl">
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 top-4"
          onClick={onSkip}
        >
          <X className="size-4" />
        </Button>

        <CardHeader className="pb-4 text-center">
          <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-full bg-primary/10">
            <Icon className="size-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">{currentStep.title}</CardTitle>
          <CardDescription>{currentStep.description}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <Progress value={progress} className="h-2" />
          
          <div className="min-h-[200px]">
            {currentStep.content}
          </div>

          <div className="flex items-center justify-between pt-4">
            <Button
              variant="ghost"
              onClick={() => setStep(Math.max(1, step - 1))}
              disabled={step === 1}
            >
              Previous
            </Button>
            
            <div className="flex gap-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`size-2 rounded-full transition-colors ${
                    i + 1 === step ? "bg-primary" : "bg-muted"
                  }`}
                />
              ))}
            </div>

            {step < totalSteps ? (
              <Button onClick={() => setStep(step + 1)}>
                Next <ArrowRight className="ml-2 size-4" />
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
